import { environment } from '../../environments/environment';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { SKIP_AUTH_HEADER } from '../common/http-inteptor/api-context';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { SubscriberLoginResp } from '../components/interface/Subscriber/SubscriberLoginResp';
import { CompanyLoginResp } from '../components/interface/Company/CompanyLoginResp';
import { BlogLoginResp } from '../components/interface/Blog/BlogLoginResp';
import { CookieService } from 'ngx-cookie-service';
import { EncryptionService } from './common/encryption.service';
import { isPlatform, Platform } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { RuntimeConfigService } from './runtime-config.service';
import { RoleService } from './role.service';
import { Role } from '../modules/admin/interface/Role';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  baseURL = environment.ASquareAPIURL;
  
  private blogUserSubject = new BehaviorSubject<BlogLoginResp | null>(null);
  private subscriberUserSubject = new BehaviorSubject<SubscriberLoginResp | null>(null);
  private companyUserSubject = new BehaviorSubject<CompanyLoginResp | null>(null);

  blogUser$ = this.blogUserSubject.asObservable();
  subscriberUser$ = this.subscriberUserSubject.asObservable();
  companyUser$ = this.companyUserSubject.asObservable();

  private headers = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: '*/*'
  });

  private options = { headers: this.headers };

  subscriberPreference: boolean;
  subscriberDetails: SubscriberLoginResp;

  constructor(private router: Router, private http: HttpClient, private cookieService: CookieService,
    private encrypt: EncryptionService, private runtimeConfigService: RuntimeConfigService, private roleService: RoleService) {
    this.syncElectronSessionFromLocalStorage();
  }

  /**
   * Electron windows share localStorage but not sessionStorage.
   * Copy remembered auth into the current window session so API calls work in parallel instances.
   */
  syncElectronSessionFromLocalStorage(): void {
    if (!this.runtimeConfigService.isElectron) {
      return;
    }

    if (sessionStorage.getItem('subscriber') || !localStorage.getItem('subscriber')) {
      return;
    }

    const keys = [
      'subscriber',
      'token',
      'subscriberID',
      'pid',
      'isSubscritionDetails',
      'businessType',
      'isPaymentCom',
    ];

    keys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value != null) {
        sessionStorage.setItem(key, value);
      }
    });
  }

  private getAuthRequestOptions() {
    return {
      headers: this.headers,
      context: new HttpContext().set(SKIP_AUTH_HEADER, true),
    };
  }

  private buildLoginParams(model: any): HttpParams {
    return new HttpParams({
      fromObject: {
        Username: String(model?.Username ?? '').trim(),
        Password: String(model?.Password ?? ''),
        RememberMe: model?.RememberMe === true || model?.RememberMe === 'true' ? 'true' : 'false',
      },
    });
  }

  private persistElectronSubscriber(subscriber: SubscriberLoginResp): void {
    localStorage.setItem('subscriber', JSON.stringify(subscriber));
    localStorage.setItem('token', subscriber.token);
    localStorage.setItem('subscriberID', subscriber.subscriberID.toString());
    localStorage.setItem('pid', subscriber.packageID.toString());
    localStorage.setItem('isSubscritionDetails', subscriber.isSubscriptionComplete ? 'true' : 'false');
    localStorage.setItem('businessType', subscriber.businessType);
    localStorage.setItem(
      'isPaymentCom',
      subscriber.packageID != null && subscriber.packageID != 0 ? '1' : '0'
    );
  }

  blogLogin(model: any) {
    const body = new HttpParams({
      fromObject: {
        Username: String(model?.Username ?? '').trim(),
        Password: String(model?.Password ?? ''),
      },
    });

    return this.http.post<BlogLoginResp>(this.baseURL + 'Auth/BlogLogin', body, this.getAuthRequestOptions()).pipe(
      map((response: BlogLoginResp) => {
        if(response) {
          this.setCurrentBlogUser(response);
        }

        return { success: response.success, userID: response.userID };
      })
    );
  }

  subscriberLogin(model: any) {
    const body = this.buildLoginParams(model);

    return this.http.post<SubscriberLoginResp>(
      this.baseURL + 'Auth/SubscriberLogin',
      body,
      this.getAuthRequestOptions()
    ).pipe(
      map((response: SubscriberLoginResp) => {
        if(response) {
          this.setCurrentSubscriber(response, model.RememberMe);
        }

        return {success: response.success, subscriptionLeft: response.subscriptionLeft, subscriberID: response.subscriberID };
      })
    );
  }

  companyLogin(model: any) {
    const body = new HttpParams({ fromObject: model });

    return this.http.post<CompanyLoginResp>(
      this.baseURL + 'Auth/CompanyLogin',
      body,
      this.getAuthRequestOptions()
    ).pipe(
      map((response: CompanyLoginResp) => {
        if(response) {
          this.setCurrentCompanyUser(response);
        }

        return response.success;
      })
    );
  }

  setCurrentBlogUser(blogUser: BlogLoginResp) {
    sessionStorage.setItem('blogUser', JSON.stringify(blogUser));
    sessionStorage.setItem('token', blogUser.token);
    sessionStorage.setItem('UserID', blogUser.userID.toString());
    this.blogUserSubject.next(blogUser);
  }

  async setCurrentSubscriber(subscriber: SubscriberLoginResp, RememberMe = false) {
    const isElectron = this.runtimeConfigService.isElectron;

    if (isPlatform('capacitor')) {
      await Preferences.set({ key: 'subscriber', value: JSON.stringify(subscriber) });
      await Preferences.set({ key: 'token', value: subscriber.token });
      await Preferences.set({ key: 'subscriberID', value: subscriber.subscriberID.toString() });
      await Preferences.set({ key: 'pid', value: subscriber.packageID.toString() });
      await Preferences.set({ key: 'isSubscritionDetails', value: subscriber.isSubscriptionComplete ? 'true' : 'false' });
      await Preferences.set({ key: 'businessType', value: subscriber.businessType });
      if (subscriber.packageID != null && subscriber.packageID != 0) {
        await Preferences.set({ key: 'isPaymentCom', value: '1' });
      }
      else {
        await Preferences.set({ key: 'isPaymentCom', value: '0' });
      }
    } else {
      sessionStorage.setItem('subscriber', JSON.stringify(subscriber));
      sessionStorage.setItem('token', subscriber.token);
      sessionStorage.setItem("subscriberID", subscriber.subscriberID.toString());
      sessionStorage.setItem("pid", subscriber.packageID.toString());
      sessionStorage.setItem("isSubscritionDetails", subscriber.isSubscriptionComplete ? 'true' : 'false');
      sessionStorage.setItem("businessType", subscriber.businessType);
      if (subscriber.packageID != null && subscriber.packageID != 0) {
        sessionStorage.setItem("isPaymentCom", "1");
      }
      else {
        sessionStorage.setItem("isPaymentCom", "0");
      }

      if (isElectron) {
        this.persistElectronSubscriber(subscriber);
      }
    }

    if (RememberMe == true) {
      if (isPlatform('capacitor')) {
        console.log('Subscriber Service: ' + JSON.stringify(subscriber));
        await Preferences.set({ key: 'subscriber', value: JSON.stringify(subscriber) });
        await Preferences.set({ key: 'token', value: subscriber.token });
        await Preferences.set({ key: 'subscriberID', value: subscriber.subscriberID.toString() });
        await Preferences.set({ key: 'pid', value: subscriber.packageID.toString() });
        await Preferences.set({ key: 'isSubscritionDetails', value: subscriber.isSubscriptionComplete ? 'true' : 'false' });
        await Preferences.set({ key: 'businessType', value: subscriber.businessType });
        if (subscriber.packageID != null && subscriber.packageID != 0) {
          await Preferences.set({ key: 'isPaymentCom', value: '1' });
        }
        else {
          await Preferences.set({ key: 'isPaymentCom', value: '0' });
        }
      } else if(isElectron) {
        this.persistElectronSubscriber(subscriber);
      } else {
        const myDate = new Date();
        myDate.setMonth(myDate.getMonth() + 6);
        this.cookieService.set('subscriber', JSON.stringify(subscriber), myDate);
        this.cookieService.set('token', subscriber.token, myDate);
        this.cookieService.set('subscriberID', subscriber.subscriberID.toString(), myDate)
        this.cookieService.set('pid', subscriber.packageID.toString(), myDate)
        this.cookieService.set('isSubscritionDetails', subscriber.isSubscriptionComplete ? 'true' : 'false', myDate)
        this.cookieService.set('businessType', subscriber.businessType, myDate)
        if (subscriber.packageID != null && subscriber.packageID != 0) {
          this.cookieService.set("isPaymentCom", "1", myDate);
        }
        else {
          this.cookieService.set("isPaymentCom", "0", myDate);
        }
      }
    }

    this.subscriberUserSubject.next(subscriber);
  }

  setCurrentCompanyUser(companyUser: CompanyLoginResp) {
    sessionStorage.setItem('companyUser', JSON.stringify(companyUser));
    sessionStorage.setItem('ctoken', companyUser.token);
    sessionStorage.setItem('RoleID', companyUser.roleID.toString());
    sessionStorage.setItem('daysLeft', companyUser.daysLeft.toString());
    this.companyUserSubject.next(companyUser);
  }

  async hasSubscriberPreference(): Promise<boolean> {
    if (this.subscriberPreference !== undefined) {
      return this.subscriberPreference;
    }

    const result = await Preferences.get({ key: 'subscriber' });
    this.subscriberPreference = !!result.value;
    return this.subscriberPreference;
  }

  async getPreferenceSubscriberDetails(): Promise<SubscriberLoginResp> {
    const result = await Preferences.get({ key: 'subscriber' });
    this.subscriberDetails = result.value ? JSON.parse(result.value) : null;
    return this.subscriberDetails;
  }

  async logout() {
    const isElectron = this.runtimeConfigService.isElectron;

    if(isPlatform('capacitor')) {
      // await Preferences.clear();
      await Preferences.remove({ key: 'subscriber' });
      await Preferences.remove({ key: 'token' });
      await Preferences.remove({ key: 'subscriberID' });
      await Preferences.remove({ key: 'pid' });
      await Preferences.remove({ key: 'isSubscritionDetails' });
      await Preferences.remove({ key: 'businessType' });
      if (await Preferences.get({ key: 'isPaymentCom' })) {
        await Preferences.remove({ key: 'isPaymentCom' });
      }
    } else if (isElectron) {
      localStorage.removeItem('subscriber');
      localStorage.removeItem('token');
      localStorage.removeItem('subscriberID');
      localStorage.removeItem('pid');
      localStorage.removeItem('isSubscritionDetails');
      localStorage.removeItem('businessType');
      localStorage.removeItem('isPaymentCom');

      // also clear any sessionStorage entries
      sessionStorage.clear();
    } else {
      this.cookieService.deleteAll();
      sessionStorage.clear();
    }
    this.blogUserSubject.next(null);
    this.subscriberUserSubject.next(null);
  }

  logoutCompany() {
    sessionStorage.removeItem('companyUser');
    sessionStorage.removeItem('ctoken');
    sessionStorage.removeItem('RoleID');
    this.companyUserSubject.next(null);
  }

  // setToken(token: string): void {
  //   sessionStorage.setItem('token', token);
  // }

  // getToken(): string | null {
  //   return sessionStorage.getItem('token');
  // }

  // IsLoggedIn() {
  //   return this.getToken() != null;
  // }

  // logout() {
  //   this.router.navigate(
  //     ['login'],
  //     { queryParams: { companyID: sessionStorage.getItem("CompanyID"),companyName:sessionStorage.getItem("CompanyName") } }
  //   );
  //   sessionStorage.removeItem('token');
  //   sessionStorage.removeItem('RoleID');
  // }

  // login(data): Observable<any> {
  //   const body = new HttpParams({ fromObject: data });
  //   const options = { headers: this.headers };
  //   return this.http.post(`${environment.ASquareAPIURL}UserLogin/AuthenticateUser`, body, options);
  // }

  GetFinancialYear(data): Observable<any> {
    const body = new HttpParams({ fromObject: data });
    const options = { headers: this.headers };
    return this.http.post(`${environment.ASquareAPIURL}UserLogin/GetFinancialYear`, body, options);
  }

  /**
   * Reads a subscriber storage key from the correct storage layer based on the current platform.
   * Supports Capacitor Preferences, Electron localStorage, web sessionStorage, and cookies.
   */
  async getItemAsync(key: string): Promise<string | null> {
    if (isPlatform('capacitor')) {
      return (await Preferences.get({ key })).value ?? null;
    }
    if (this.runtimeConfigService.isElectron) {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    }
    return sessionStorage.getItem(key) || this.cookieService.get(key) || null;
  }

  /**
   * Synchronous variant – does NOT support Capacitor (which is async-only).
   * Use getItemAsync() for full cross-platform support.
   */
  getItemSync(key: string): string | null {
    if (this.runtimeConfigService.isElectron) {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    }
    return sessionStorage.getItem(key) || this.cookieService.get(key) || null;
  }

  hasStoredSubscriber(): boolean {
    const subscriberJson = this.getItemSync('subscriber');
    if (!subscriberJson) {
      return false;
    }

    try {
      const subscriber: SubscriberLoginResp = JSON.parse(subscriberJson);
      return !!subscriber?.subscriberID && !!subscriber?.token;
    } catch {
      return false;
    }
  }

  navigateToSubscriberHome(): void {
    const subscriberJson = this.getItemSync('subscriber');
    if (!subscriberJson) {
      return;
    }

    try {
      const subscriber: SubscriberLoginResp = JSON.parse(subscriberJson);
      if (!subscriber?.subscriberID) {
        return;
      }

      this.syncElectronSessionFromLocalStorage();
      this.router.navigate(['view-company'], {
        queryParams: { subscriberID: subscriber.subscriberID },
      });
    } catch {
      return;
    }
  }

}
